/**
 * Synthesize backend-format vehicle paths from route_stops rows so the
 * EXISTING geoJson pipeline (createRoutesGeoJson / addGeoJsonResults in
 * geoJsonFunctions.js) can rebuild the result layers after an edit without any
 * changes of its own.
 *
 * The backend's paths are [{vehicle_id, path}] where each leg carries
 * from/to ids, names, coordinates, colors, a leg UUID, and 'From Action' /
 * 'To Action' ARRAYS. createRoutesGeoJson composes the routePoint for stop i
 * from [...leg[i-1]['To Action'], ...leg[i]['From Action']], so we put each
 * stop's action into its outgoing leg's 'From Action' and leave 'To Action'
 * empty except on the final leg (whose 'To Action' feeds the terminal point).
 */

import { generateUUID } from 'store/functions/geoJsonFunctions.js';
import { groupRouteStops } from './evaluator';

const coordOf = (locationsById, locationId, field) => {
  const loc = locationId != null ? locationsById.get(String(locationId)) : null;
  return loc ? loc[field] ?? null : null;
};

export function synthesizePathLegs(routeStops, locations) {
  const locationsById = new Map((locations || []).map((l) => [String(l.UUID), l]));
  const byVehicle = groupRouteStops(routeStops);
  const paths = [];

  for (const [vehicleId, rows] of byVehicle) {
    if (rows.length < 2) continue;
    const legs = [];
    for (let i = 0; i < rows.length - 1; i++) {
      const from = rows[i];
      const to = rows[i + 1];
      const isLastLeg = i === rows.length - 2;
      const fromLoc = locationsById.get(String(from.locationId));
      legs.push({
        Leg: i,
        fromLocationId: from.locationId,
        fromLocationName: from.locationName ?? null,
        fromLatitude: coordOf(locationsById, from.locationId, 'latitude'),
        fromLongitude: coordOf(locationsById, from.locationId, 'longitude'),
        toLocationId: to.locationId,
        toLocationName: to.locationName ?? null,
        toLatitude: coordOf(locationsById, to.locationId, 'latitude'),
        toLongitude: coordOf(locationsById, to.locationId, 'longitude'),
        color: from.color ?? to.color ?? null,
        fromLocationColor: (fromLoc && fromLoc.color) || null,
        UUID: generateUUID(),
        'From Action': from.action ? [from.action] : [],
        'To Action': isLastLeg && to.action ? [to.action] : [],
      });
    }
    paths.push({ vehicle_id: vehicleId, path: legs });
  }
  return paths;
}
